/**
 * Orca Task Detail route — eagerly registers the route + `beforeLoad` SSR
 * loader but defers component + atom + xterm imports via `.lazy.tsx`. This
 * keeps the orca task-detail chunk (xterm.js, terminal protocol, etc.) out
 * of the bundle for users on every other route.
 *
 * Namespaced under `/orca/task/$issueIdentifier`, so the segment is always a
 * task id — no root catch-all to defend. The `beforeLoad` guard still rejects
 * any segment that is not a Linear issue identifier with `notFound()` before any
 * SSR read, so `/task/foo` renders the root not-found page instead.
 *
 * @since 1.0.0
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { isLinearIssueIdentifier } from "~/lib/orca-issue-identifier.ts";
import type { BeforeLoadArgs } from "~/lib/orca-ssr.ts";
import { orcaTaskDetailBeforeLoad } from "~/lib/orca-ssr.ts";
import { orcaloopsStylesheetLinks } from "~/lib/orcaloops-head.ts";

/**
 * Rejects non-identifier segments before any SSR read; delegates real ones to
 * the SSR loader. Exported so it is unit-testable without the route machinery,
 * mirroring `orcaLayoutBeforeLoad` in `routes/orca/route.tsx`.
 *
 * @since 1.0.0
 */
export const orcaTaskDetailRouteBeforeLoad = (args: BeforeLoadArgs) => {
  if (!isLinearIssueIdentifier(args.params?.issueIdentifier)) {
    throw notFound();
  }
  return orcaTaskDetailBeforeLoad(args);
};

/**
 * The orcaloops seam's stylesheet as a render-blocking head `<link>` — the v5
 * frame (OL-394) must paint styled on SSR, never after the lazy chunk loads.
 *
 * @since 1.0.0
 */
export const taskDetailHead = () => ({ links: orcaloopsStylesheetLinks });

export const Route = createFileRoute("/orca/task/$issueIdentifier")({
  beforeLoad: orcaTaskDetailRouteBeforeLoad,
  head: taskDetailHead,
});
